import { useCallback, useEffect, useState } from "react";
import { useAccount, useSignMessage, useSendTransaction, useWaitForTransactionReceipt } from "wagmi";
import { parseEther, type Address } from "viem";

const KEY = "piggy-radar-session";
const ETH_KEY = "piggy-radar-eth-paid"; // wallet that paid ETH for access (soft unlock)

export interface RadarSession {
    token: string;
    wallet: string;
    via: string;
    expiresAt: number | null;
    sessionExp: number; // local ms when the HMAC session token itself expires
}

export interface RadarRequirements {
    stakePiggy: string;
    ethPrice: string;
    creatorWallet: string;
}

function loadSession(): RadarSession | null {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const s = JSON.parse(raw) as RadarSession;
        if (s.sessionExp < Date.now()) { localStorage.removeItem(KEY); return null; }
        return s;
    } catch { return null; }
}

/// Piggy Radar wallet auth. Sign a login message → the server checks
/// entitlement (staked PIGGY or ETH paid) and returns a session token that
/// unlocks the premium feed. Persists the token so a signature isn't needed
/// every visit. Returns the bearer token for feed requests.
export function useRadarAuth() {
    const { address } = useAccount();
    const { signMessageAsync } = useSignMessage();
    const [session, setSession] = useState<RadarSession | null>(() => loadSession());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [requirements, setRequirements] = useState<RadarRequirements | null>(null);
    const [stakedPiggy, setStakedPiggy] = useState<string | null>(null);

    // ETH-payment unlock (soft, client-side): pay the creator wallet, then this
    // wallet is unlocked locally. When server gating is enabled, the server also
    // verifies the on-chain payment for the hard gate.
    const { sendTransactionAsync } = useSendTransaction();
    const [ethError, setEthError] = useState<string | undefined>();
    const [payHash, setPayHash] = useState<`0x${string}` | undefined>();
    const receipt = useWaitForTransactionReceipt({ hash: payHash });
    const [ethPaidWallet, setEthPaidWallet] = useState<string | null>(() => localStorage.getItem(ETH_KEY));
    const ethUnlocked = Boolean(address && ethPaidWallet && ethPaidWallet.toLowerCase() === address.toLowerCase());

    // Persist the unlock once the payment confirms.
    useEffect(() => {
        if (receipt.isSuccess && address) {
            localStorage.setItem(ETH_KEY, address);
            setEthPaidWallet(address);
        }
    }, [receipt.isSuccess, address]);

    const payEth = useCallback(async (to: string, amountEth: number) => {
        if (!address) { setEthError("Connect your wallet first."); return; }
        if (!/^0x[0-9a-fA-F]{40}$/.test(to) || !(amountEth > 0)) { setEthError("Pricing unavailable — try again shortly."); return; }
        setEthError(undefined);
        try {
            const hash = await sendTransactionAsync({ to: to as Address, value: parseEther(amountEth.toFixed(18)) });
            setPayHash(hash);
        } catch (e) {
            setEthError((e as Error)?.message?.includes("reject") ? "Payment rejected." : "Could not send payment. Try again.");
        }
    }, [address, sendTransactionAsync]);

    // Drop a session that doesn't match the connected wallet.
    useEffect(() => {
        if (session && address && session.wallet.toLowerCase() !== address.toLowerCase()) {
            localStorage.removeItem(KEY);
            setSession(null);
        }
    }, [address, session]);

    const unlock = useCallback(async () => {
        if (!address) { setError("Connect your wallet first."); return; }
        setBusy(true); setError(undefined); setRequirements(null);
        try {
            const issued = Math.floor(Date.now() / 1000);
            const message = `Piggy Radar login\nWallet: ${address}\nIssued: ${issued}`;
            const signature = await signMessageAsync({ message });
            const res = await fetch("/.netlify/functions/radar-login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ wallet: address, issued, signature }),
            });
            const d = await res.json();
            if (d?.gatingDisabled) { setError(undefined); return; } // feed is open anyway
            if (d?.entitled && typeof d.token === "string") {
                const s: RadarSession = {
                    token: d.token, wallet: address, via: String(d.via ?? "stake"),
                    expiresAt: d.expiresAt ?? null,
                    sessionExp: Date.now() + (Number(d.sessionTtlSec ?? 3600) * 1000),
                };
                localStorage.setItem(KEY, JSON.stringify(s));
                setSession(s);
            } else {
                setRequirements(d?.requirements ?? null);
                setStakedPiggy(typeof d?.stakedPiggy === "string" ? d.stakedPiggy : null);
                setError("Not yet eligible — stake PIGGY or pay ETH to unlock.");
            }
        } catch (e) {
            setError((e as Error)?.message?.includes("reject") ? "Signature rejected." : "Could not unlock. Try again.");
        } finally {
            setBusy(false);
        }
    }, [address, signMessageAsync]);

    const logout = useCallback(() => {
        localStorage.removeItem(KEY); localStorage.removeItem(ETH_KEY);
        setSession(null); setEthPaidWallet(null);
    }, []);

    return {
        session, token: session?.token ?? null, unlock, logout, busy, error, requirements, stakedPiggy,
        payEth, ethUnlocked, ethError, ethPaying: Boolean(payHash) && receipt.isLoading,
    };
}
